// Prefix for errors raised explicitly via `raiseError`, which have already been handled.
export const SELF_REPORTED_ERROR = "*** JS ERROR: ";

const RESIZE_OBSERVER_ERROR_MSG =
  "ResizeObserver loop completed with undelivered notifications.";

// These are strange errors we've been seeing from machines that have dodgy plugins. It happens
// constantly. We must ignore them.
// Also some chrome errors (eg ntp) when refreshing the page. https://issues.chromium.org/issues/41463624
const PLUGIN_ERROR_STRINGS = [
  "Can't find variable: dataKeys",
  "QUOTA_EXCEEDED_ERR",
  "ilividtoolbarguid",
  "ntp is not defined",
  "__gCrWeb",
  "similarproducts",
  "superfishjs",
  "www.google-analytics.com",
  "datafastguru.info",
  "_watcherReady",
  "topbuyer",
  "shoppingate",
  "checkpoint",
  "akamaihd.net",
  "sendmessagebox.com",
  // If url, or the stack, contains "chrome://" or "chrome-extension://" then it's a dodgy chrome
  // extension.
  "chrome://",
  "chrome-extension://",
  "document.activeElement",
  ".php?",
  // Block the chrome mobile form filler hitting trustpilot, but also any other marketing
  // injected iframes.
  "Blocked a frame with origin",
  "Object Not Found Matching Id",
];

export const isDodgyPluginError = (message: unknown, url?: unknown, stack?: unknown) => {
  const haystack = `${message}${url}${stack}`;
  return PLUGIN_ERROR_STRINGS.some(pluginString => haystack.includes(pluginString));
};

// Only checkable once static_data has loaded; until then we assume the script is ours.
const isFromAnotherDomain = (url: string) => {
  const domain = window.static_data?.domain;
  if (!domain) { return false; }

  const ourDomains = [
    domain,
    window.location.host,
    window.static_data.build_artefacts_bucket_cloudfront_domain,
  ].filter((d): d is string => !!d);
  return !ourDomains.some(d => url.includes(d));
};

const isNotOurCodeError = (url: unknown, line?: number) => {
  const urlString = url ? `${url}`.trim() : "";
  if (!urlString) {
    // If the url is blank, it is a script from an alternative domain (injected by a plugin?)
    return true;
  }
  if (line === 1 && !urlString.includes(".js")) {
    // If the url is not a JS file, and the error is on line 1, then it is an inlined plugin script.
    return true;
  }
  // If the url doesn't include our domain, it's a document injected script.
  return isFromAnotherDomain(urlString);
};

export const isReportableError = (
  message: unknown,
  url?: unknown,
  line?: number,
  stack?: unknown
) => {
  const msg = `${message}`;

  return !(
    // Errors raised via `raiseError` have already been handled.
    msg.includes(SELF_REPORTED_ERROR)
    || isDodgyPluginError(msg, url, stack)
    || isNotOurCodeError(url, line)
    || navigator.userAgent.includes("UCBrowser")
    // Talkdesk's webchat widget generates many of these. They don't impact functionality, so we
    // ignore them until Talkdesk provides a fix. See https://firstbanco.atlassian.net/browse/PT-8518.
    || msg === RESIZE_OBSERVER_ERROR_MSG
  );
};
